"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

export const CatalogPagination = ({
    currentPage,
    totalPages,
    onPageChange,
}: Props) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 3; i++) pages.push(i);
                pages.push("...");
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push("...");
                for (let i = totalPages - 2; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push("...");
                pages.push(currentPage);
                pages.push("...");
                pages.push(totalPages);
            }
        }
        return pages;
    };

    const pages = getPageNumbers();

    return (
        <div className="mt-8 sm:mt-16 flex justify-center items-center gap-1 sm:gap-4">
            {/* PREV */}
            <button
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
                className={cn(
                    "h-8 w-8 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center border-2 transition-all",
                    currentPage === 1
                        ? "opacity-30 cursor-not-allowed border-primary-200"
                        : "border-primary-600 text-primary-600 hover:bg-primary-100 hover:scale-105 active:scale-95"
                )}
            >
                <ChevronLeft className="h-4 w-4 sm:h-6 sm:w-6" />
            </button>

            {/* PAGES */}
            {pages.map((page, index) => (
                <button
                    key={index}
                    disabled={page === "..."}
                    onClick={() => typeof page === "number" && onPageChange(page)}
                    className={cn(
                        "h-8 w-8 sm:h-10 sm:w-10 rounded-lg border-2 text-xs sm:text-xl font-bold transition-all",
                        currentPage === page
                            ? "bg-primary-600 border-primary-600 text-white shadow-lg"
                            : page === "..."
                                ? "border-transparent text-primary-400 cursor-default"
                                : "border-primary-600 text-primary-600 hover:bg-primary-100 hover:scale-105 active:scale-95"
                    )}
                >
                    {page}
                </button>
            ))}

            {/* NEXT */}
            <button
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
                className={cn(
                    "h-8 w-8 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center border-2 transition-all",
                    currentPage === totalPages
                        ? "opacity-30 cursor-not-allowed border-primary-200"
                        : "border-primary-600 text-primary-600 hover:bg-primary-100 hover:scale-105 active:scale-95"
                )}
            >
                <ChevronRight className="h-4 w-4 sm:h-6 sm:w-6" />
            </button>
        </div>
    );
};
